import { Log } from './logging'
import { nextGuid } from './uuid-utils'

type Options = {
  timeoutMs?: number
  retries?: number
  verbose?: boolean
  retryId?: string
}

export class TimeoutError extends Error {
  public readonly name = 'TimeoutError'
}

/**
 * Races `promise` against a timeout. Resolves with the promise's value if it
 * settles first; rejects with `TimeoutError` after `ms` milliseconds.
 *
 * Attaches a no-op `.catch` to `promise` so that late rejections (e.g. a
 * Puppeteer `page.pdf` that eventually fails after we've already timed out)
 * do NOT surface as unhandled rejections.
 */
export const withTimeout = <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  // Drain any late rejection from the underlying promise so it doesn't surface
  // as unhandled after the timeout branch has already settled the race.
  promise.catch(() => {})

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(`${label} timed out after ${ms}ms`)), ms)
  })

  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer)
  })
}

export const withRetry = async <T>(fn: () => Promise<T>, options: Options = {}): Promise<T> => {
  const { timeoutMs = 1000, retries = 5, verbose = false, retryId = nextGuid() } = options
  let attempt = 0

  if (verbose) {
    Log.info(`Running with retries: ${retryId}`)
  }
  while (attempt < retries) {
    try {
      const res = await fn()
      if (verbose) {
        Log.info(`Completed running with retries: ${retryId}. Final attempt: ${attempt + 1}`)
      }
      return res
    } catch (e) {
      if (attempt >= retries - 1) {
        if (verbose) {
          Log.error(`Exceeded maximum retries for ${retryId}`)
        }
        throw e
      }

      const delay = timeoutMs * Math.pow(2, attempt)
      const jitter = Math.random() * delay
      const finalDelay = delay + jitter
      if (verbose) {
        Log.warn(`Attempt ${attempt + 1} for ${retryId} failed. Retrying in ${finalDelay.toFixed(0)}ms...`)
      }
      await new Promise(resolve => setTimeout(resolve, finalDelay))
      attempt++
    }
  }
  throw new Error('Exceeded maximum retries')
}
